import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import RolForm from "./Rolform";
import { useRoles } from "../../hooks/useRoles"; // Hook personalizado
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import { useAuthContext } from "../../context/AuthProvider";

const RolesDashboard = () => {
  const [open, setOpen] = useState(false);
  const [editRol, setEditRol] = useState(null); // Estado para edición
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  // Hook para obtener los roles y las mutaciones
  const { user } = useAuthContext();
  const { roles, isLoading, error, createMutation, updateMutation, deleteMutation } = useRoles();

  // Mostrar mensaje de alerta
  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  // Función para manejar la creación o edición
  const handleSubmitRol = (data) => {
    if (editRol) {
      updateMutation.mutate(
        { id: editRol.rolid, data: { ...data, updbyid: user.EmpID } },
        {
          onSuccess: () => {
            showAlert("Rol actualizado con éxito");
            setOpen(false);
            setEditRol(null);
          },
          onError: (error) => {
            showAlert(error.message || "Error al actualizar el Rol", "error");
          },
        }
      );
    } else {
      data.crebyid = user.EmpID;
      createMutation.mutate(data, {
        onSuccess: () => {
          showAlert("Rol creado con éxito");
          setOpen(false);
        },
        onError: (error) => {
          showAlert(error.message || "Error al crear el Rol", "error");
        },
      });
    }
  };

  // Función para desactivar una ventana
  const handleDeactivateRol = (id) => {
    deleteMutation.mutate(id, {
      onSuccess: () => showAlert("Rol desactivado con éxito"),
      onError: (error) => showAlert(error.response.data.error || "Error al desactivar el Rol", "error"),
    });
  };

  if (isLoading) return <Typography>Cargando roles...</Typography>;
  if (error) return <Typography>Error al cargar los roles: {error.message}</Typography>;

  return (
    <Paper sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Roles</Typography>
        <Tooltip title="Agregar Rol">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditRol(null);
              setOpen(true);
            }}
          >
            Nuevo Rol
          </Button>
        </Tooltip>
      </Box>

      {/* Lista de roles */}
      <List>
        {roles.map((rol) => (
          <ListItem
            key={rol.rolid}
            sx={{ bgcolor: "white", mb: 1, borderRadius: 1, boxShadow: 1 }}
          >
            <ListItemText
              primary={rol.rolnom}
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary">
                    Descripcion: {rol.roldes}
                  </Typography>
                </>
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Estado de la ventana */}
              <Chip
                label={rol.estreg}
                color={rol.estreg === "A" ? "success" : "error"}
                size="small"
              />
              {/* Botón para editar */}
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditRol(rol);
                    setOpen(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {/* Botón para desactivar */}
              {rol.estreg === "A" && (
                <Tooltip title="Desactivar">
                  <IconButton
                    size="small"
                    onClick={() => handleDeactivateRol(rol.rolid)}
                  >
                    <BlockIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Formulario para crear/editar ventanas */}
      {open && (
        <RolForm
          open={open}
          handleClose={() => {
            setOpen(false);
            setEditRol(null);
          }}
          onSubmit={handleSubmitRol}
          defaultValues={editRol || {}}
        />
      )}

      {/* Notificaciones */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default RolesDashboard;
